/**
 * The last frame of someone's video, held while their call moves between the
 * direct connection and the bridge (web).
 *
 * lib-jitsi-meet swaps the remote tracks when a call moves: the old track is
 * removed and a new one, with no frame yet, takes its place. The pane's video
 * element is detached from the old track and stays blank until the new one
 * plays, which read as a black flash on every move (at the start of every
 * two-person call, and whenever a third person comes or goes).
 *
 * Just before the old track leaves the store (vrs-layout/middleware.web.ts,
 * ahead of the reducer), its element still shows the last frame: it is drawn
 * into a canvas here, and VRSLayout lays that canvas under the pane's video
 * until the new track's first frame has played (shouldShowHeldFrame).
 *
 * Only a frame of someone still in the room is kept (a person leaving is not
 * a move), one per person, released when their new video plays, when they
 * leave, when the conference ends, or after HELD_FRAME_MAX_MS.
 */
import { HELD_FRAME_MAX_MS } from './heldFrame';
/** Wide enough for a pane, small enough to copy in a few milliseconds. */
const MAX_CAPTURE_WIDTH = 960;
const frames = new Map();
const listeners = new Set();
let version = 0;
function changed() {
    version += 1;
    listeners.forEach(listener => {
        try {
            listener();
        }
        catch { /* a listener's failure is its own */ }
    });
}
/** A video element of the track with a frame on it, if any. */
function frameSource(containers) {
    if (!Array.isArray(containers) || typeof HTMLVideoElement === 'undefined') {
        return undefined;
    }
    return containers.find((element) => element instanceof HTMLVideoElement
        && element.readyState >= 2 && element.videoWidth > 0 && element.videoHeight > 0);
}
/**
 * Draws the last frame of a remote video track that is about to be removed,
 * for `participantId`. Returns whether a frame was held.
 *
 * @param {string} participantId - Whose video it is.
 * @param {Object} jitsiTrack - The outgoing JitsiRemoteTrack (its `containers`
 * are the elements it is attached to).
 * @param {number} [now] - The time, for tests.
 * @returns {boolean}
 */
export function holdRemoteFrame(participantId, jitsiTrack, now = Date.now()) {
    const video = frameSource(jitsiTrack?.containers);
    if (!participantId || !video || typeof document === 'undefined') {
        return false;
    }
    try {
        const scale = Math.min(1, MAX_CAPTURE_WIDTH / video.videoWidth);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
        canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) {
            return false;
        }
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.setAttribute('aria-hidden', 'true');
        const held = { at: now, canvas };
        frames.set(participantId, held);
        changed();
        // No new frame in time: the pane goes back to what it shows without one.
        setTimeout(() => {
            if (frames.get(participantId) === held) {
                frames.delete(participantId);
                changed();
            }
        }, HELD_FRAME_MAX_MS + 50);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * The held frame for `participantId`, unless it is older than
 * HELD_FRAME_MAX_MS (then it is released).
 *
 * @param {string} participantId - Whose frame.
 * @param {number} [now] - The time, for tests.
 * @returns {Object|undefined}
 */
export function getHeldFrame(participantId, now = Date.now()) {
    const held = participantId ? frames.get(participantId) : undefined;
    if (held && now - held.at > HELD_FRAME_MAX_MS) {
        frames.delete(participantId);
        return undefined;
    }
    return held;
}
/**
 * Drops the held frame for one person, or for everyone.
 *
 * @param {string} [participantId] - Whose frame; everyone's when absent.
 * @param {number} [heldBefore] - Only a frame held at or before this time
 * (a frame held again since, for a newer move, stays).
 * @returns {void}
 */
export function releaseHeldFrame(participantId, heldBefore) {
    let removed = false;
    if (participantId === undefined) {
        removed = frames.size > 0;
        frames.clear();
    }
    else {
        const held = frames.get(participantId);
        if (held && (heldBefore === undefined || held.at <= heldBefore)) {
            removed = frames.delete(participantId);
        }
    }
    if (removed) {
        changed();
    }
}
/** Notified whenever a frame is held or released. */
export function subscribeHeldFrames(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
/** Changes on every hold or release, for useSyncExternalStore. */
export function getHeldFramesVersion() {
    return version;
}
